import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { VariablePillView } from './variable-pill.view';

export const VARIABLE_PILL_NAME = 'variablePill';

export type VariablePillAttrs = {
  /** Binding ref, e.g. `Webhook/pull_request/title`. */
  ref: string;
  /** Display label for root refs where the saved ref uses a disambiguated source key. */
  label?: string;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variablePill: {
      /** Insert a variable pill referencing a previous step's output field. */
      insertVariablePill: (attrs: VariablePillAttrs) => ReturnType;
    };
  }
}

/**
 * An atomic inline node standing in for a `{{ref}}` template token. Renders as a
 * pill via {@link VariablePillView}; serialized back to `{{ref}}` by the editor.
 */
export const VariablePill = Node.create({
  name: VARIABLE_PILL_NAME,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      ref: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-ref') ?? '',
        renderHTML: (attributes) => ({ 'data-ref': String(attributes['ref']) }),
      },
      // Optional for backwards compatibility with existing saved pills that only
      // stored data-ref.
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          const label = attributes['label'];
          return typeof label === 'string' && label !== ''
            ? { 'data-label': label }
            : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${VARIABLE_PILL_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes({ 'data-type': VARIABLE_PILL_NAME }, HTMLAttributes),
    ];
  },

  renderText({ node }) {
    return `{{${String(node.attrs['ref'])}}}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariablePillView);
  },

  addCommands() {
    return {
      insertVariablePill:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: VARIABLE_PILL_NAME,
            attrs,
          }),
    };
  },
});
